import {
  BaseRecord,
  CrudOperators,
  HttpError,
  LogicalFilter,
  useTable as useRefineTable,
  useTableProps,
  useTableReturnType,
} from '@refinedev/core';
import {
  ColumnDef,
  ColumnFiltersState,
  getCoreRowModel,
  PaginationState,
  SortingState,
  Table,
  TableOptions,
  useReactTable as useTanstackReactTable,
  VisibilityState,
} from '@tanstack/react-table';
import React, {useCallback, useMemo} from 'react';

export type ConvertDataOptions = {
  refine_to_tanstack?: (data: any) => any;
  tanstack_to_refine?: (data: any) => any;
};
/**
 * Custom hook to create a data table with advanced features such as pagination, sorting, row selection, and column visibility.
 *
 * @template TData - The type of data records.
 * @template TError - The type of error that can occur.
 * @param {ColumnDef<TData>[]} columns - The column definitions for the table.
 * @param {useTableProps<TData, TError, TData>} refineProps - The properties for the table.
 * @returns {Table<TData>} - The configured table instance.
 *
 * @example
 * const columns = [
 *   { accessorKey: 'id', header: 'ID' },
 *   { accessorKey: 'name', header: 'Name' },
 * ];
 * const props = { resource: 'users', initialPageSize: 10 };
 * const table = useDataTable(columns, props);
 */
const useRefineDataTable = <TData extends BaseRecord, TError extends HttpError>(
  refineProps: useTableProps<TData, TError, TData>,
  columns?: ColumnDef<TData>[],
) => {
  const refineTable = useRefineTable(refineProps);
  const data = refineTable.tableQuery.data?.data || [];
  const total = refineTable.tableQuery.data?.total || 0;  
  const {current, setCurrent, setSorters, pageSize, sorters, setPageSize, filters, setFilters} =
    refineTable;

  // pagination
  const pagination = useMemo<PaginationState>(
    () => ({
      pageIndex: current - 1,
      pageSize,
    }),
    [current, pageSize],
  );

  const onPaginationChange = (
    updaterOrValue: PaginationState | ((old: PaginationState) => PaginationState),
  ) => {
    const newPagination =
      typeof updaterOrValue === 'function' ? updaterOrValue(pagination) : updaterOrValue;
    setCurrent(newPagination.pageIndex + 1);
    setPageSize(newPagination.pageSize);
  };

  // sorting
  const sorting = useMemo<SortingState>(
    () => [
      {
        id: sorters?.[0]?.field,
        desc: sorters?.[0]?.order === 'desc',
      },
    ],
    [sorters],
  );

  const onSortingChange = (
    updaterOrValue: SortingState | ((old: SortingState) => SortingState),
  ) => {
    const newSorting =
      typeof updaterOrValue === 'function' ? updaterOrValue(sorting) : updaterOrValue;
    setSorters([{field: newSorting[0].id, order: newSorting[0].desc ? 'desc' : 'asc'}]);
  };

  // global filter
  const globalFilter = useMemo(
    () => filters?.find(filter => (filter as LogicalFilter).field === '_search')?.value || '',
    [filters],
  );

  // global filter change handler
  const onGlobalFilterChange = (value: string) => {
    setFilters(prevFilters => {
      const otherFilters = prevFilters.filter(
        filter => (filter as LogicalFilter).field !== '_search',
      );
      return [...otherFilters, {field: '_search', operator: 'contains', value}];
    });
  };

  //// column filters
  // convert from refine column filters to tanstack logical filters
  const columnFilters = useMemo<ColumnFiltersState>(() => {
    const output: ColumnFiltersState = [];
    if (!columns) return output;

    columns.forEach(column => {
      const filter = filters?.find(
        filter => column.id && (filter as LogicalFilter).field === column.id,
      ) as LogicalFilter;
      if (filter)
        output.push({
          id: filter.field,
          value: filter.value || '',
        });
    });

    return output;
  }, [columns, filters]);

  // convert from tanskstack column filters to refine logical filters
  const onColumnFiltersChange = useCallback(
    (updaterOrValue: ColumnFiltersState | ((old: ColumnFiltersState) => ColumnFiltersState)) => {
      if (!columns) return;
      const newFilter =
        typeof updaterOrValue === 'function' ? updaterOrValue(columnFilters) : updaterOrValue;

      const newRefineFilters = newFilter.map(filter => {
        const col = columns.find(column => column.id === filter.id);
        let operator: CrudOperators = 'eq';
        if (col?.filterFn === 'includesString') operator = 'contains';

        return {
          field: filter.id,
          operator,
          value: String(filter.value),
        };
      });

      if (globalFilter)
        newRefineFilters.push({
          field: '_search',
          operator: 'contains' as const,
          value: globalFilter,
        });
      setFilters(newRefineFilters, 'replace');
    },
    [columns, columnFilters, globalFilter, setFilters],
  );

  // others
  const [rowSelection, setRowSelection] = React.useState({});
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({});
  // const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);

  return {
    refineTable,
    data,
    total,
    pagination,
    onPaginationChange,
    sorting,
    onSortingChange,
    globalFilter,
    onGlobalFilterChange,
    columnFilters,
    onColumnFiltersChange,
    rowSelection,
    setRowSelection,
    columnVisibility,
    setColumnVisibility,
  };
};

export default useRefineDataTable;
